(() => {
  'use strict';

  const initialise = () => document.querySelectorAll('[data-dream-studio]').forEach((studio) => {
    if (studio.dataset.editorPresentation === 'ready') return;
    const editor = studio.querySelector('[data-editor-root]');
    const canvas = editor?.querySelector('[data-editor-canvas]');
    if (!editor || !canvas) return;
    studio.dataset.editorPresentation = 'ready';

    const heading = studio.querySelector('[data-ux-stage-heading]');
    const headingText = heading?.querySelector('[data-ux-stage-heading-text]') || heading;
    const sourceHeading = heading?.querySelector('[data-ux-source-heading]');
    const progress = studio.querySelector('[data-ux-stage-progress]');
    const progressText = progress?.querySelector?.('strong') || progress;
    const navigation = studio.querySelector('[data-ux-stage-navigation]');
    const selectedSummary = studio.querySelector('[data-selected-summary]');
    const guidedIllustration = studio.querySelector('[data-guided-illustration]');
    const ideaInput = studio.querySelector('[data-idea-input]');
    const sourceIntro = studio.querySelector('[data-source-intro]');
    const restored = editor.querySelector('[data-ux-editor-restored]');
    const exportButton = editor.querySelector('[data-editor-export]');
    const uploadStatus = studio.querySelector('[data-upload-status]');
    const retry = studio.querySelector('[data-ux-editor-retry]');
    const nudges = [...editor.querySelectorAll('[data-ux-editor-nudge]')];
    const centre = editor.querySelector('[data-editor-centre]');
    const position = editor.querySelector('.ux-editor__position');
    const fitControls = editor.querySelector('[data-ux-editor-fit-controls]');
    const overlay = editor.querySelector('[data-editor-image-overlay]');
    const overlayControls = editor.querySelector('[data-editor-overlay-controls]');
    const overlayToggle = editor.querySelector('[data-editor-overlay-toggle]');
    const overlayText = editor.querySelector('[data-editor-overlay-text]');
    const artboard = canvas.parentElement;
    const artwork = editor.querySelector('.ux-editor__artwork');
    const imageTips = artwork?.querySelector('.ux-editor__tips');
    const artworkToggle = editor.querySelector('[data-editor-artwork-toggle]');
    const artworkPreview = editor.querySelector('[data-editor-artwork-preview]');
    const textPanel = editor.querySelector('[data-dim-text-panel]');
    const textControls = editor.querySelector('[data-dim-text-controls]');
    const imagePanel = editor.querySelector('[data-dim-image-panel]');
    const forward = editor.querySelector('.ux-editor__forward');
    const prototypeE = editor.dataset.eEditorPresentation === 'true';
    if (prototypeE) studio.dataset.eEditorPresentation = 'true';
    const taskButtons = prototypeE ? [...editor.querySelectorAll('[data-e-editor-task]')]
      .filter(button => ['words', 'layout'].includes(button.dataset.eEditorTask)) : [];
    const taskNavigation = editor.querySelector('[data-e-editor-navigation]');
    const catalogue = editor.querySelector('[data-e-editor-catalogue]');
    const catalogueImage = editor.querySelector('[data-e-editor-catalogue-image]');
    const designSummary = editor.querySelector('[data-e-editor-summary]');
    const taskGuidance = editor.querySelector('[data-e-editor-guidance]');
    const textCount = editor.querySelector('[data-dim-text-count]');
    const selectedImage = studio.querySelector('[data-selected-image]');
    const selectedName = studio.querySelector('[data-selected-name]');
    const selectedVariant = studio.querySelector('[data-selected-variant]');
    const canvasTabIndex = canvas.getAttribute('tabindex');
    let editingTask = 'overview';
    let largerEditingView = false;
    let artworkCollapsed = false;
    let overlayMap = [];
    try { overlayMap = JSON.parse(editor.querySelector('[data-image-overlay-map]')?.textContent || '[]'); } catch (_) { /* Optional overlay stays unavailable. */ }
    let overlayContext = '';
    let activeOverlay = null;
    let loadedOverlay = null;
    let overlayShown = false;
    let overlayRequest = 0;
    let stickyFrame = 0;
    let focusVisibilityPending = false;
    const compactLayout = globalThis.matchMedia?.('(max-width: 820px)');
    const touchLayout = globalThis.matchMedia?.('(pointer: coarse)');
    const syncTaskGuidance = () => {
      if (!prototypeE || !taskGuidance) return;
      const wordsOnly = studio.dataset.wordsOnly === 'true';
      const hasText = Number.parseInt(textCount?.textContent || '0', 10) > 0;
      const compact = editor.dataset.eEditorCompact === 'true';
      let copy;
      if (editingTask === 'overview') copy = wordsOnly && !hasText
        ? 'Add some text to start your design, then preview it on your product.'
        : 'Happy with your design? Preview it on your product, or choose an option below to make changes.';
      else if (editingTask === 'words') copy = !hasText
        ? compact ? 'Add a text block, then open the larger view to move it.'
          : wordsOnly ? 'Add a text block to start your design.' : 'Add a text block to your design. Your picture stays in place.'
        : compact ? 'Open the larger view to move your text.'
          : `Select a text block, then drag it to move it.${wordsOnly ? '' : ' Your picture stays in place.'}`;
      else if (wordsOnly) copy = hasText
        ? 'Use the controls below to position your text. To drag it, choose Add or edit text.'
        : 'Add text in Add or edit text, then use the controls below to position it.';
      else copy = compact ? 'Open the larger view to move or resize your picture.'
        : touchLayout?.matches || globalThis.navigator?.maxTouchPoints > 0
          ? 'Drag your picture to move it. Use two fingers to resize it, or use the controls below.'
          : 'Drag your picture to move it, or use the controls below.';
      if (taskGuidance.textContent !== copy) taskGuidance.textContent = copy;
    };
    const placeCentre = () => {
      if (!centre || !position || !fitControls) return;
      const target = compactLayout?.matches ? fitControls : position;
      const before = target.querySelector(compactLayout?.matches ? '[data-editor-reset]' : '[data-ux-editor-nudge="ArrowRight"]');
      if (centre.parentElement !== target) target.insertBefore(centre, before);
    };
    const stickyHeaderBottom = () => Array.from(document.querySelectorAll('header, [data-site-header], .header-wrapper'))
      .reduce((bottom, header) => {
        const rect = header.getBoundingClientRect?.();
        if (!rect || rect.bottom <= 0 || rect.top >= window.innerHeight) return bottom;
        return Math.max(bottom, Math.min(rect.bottom, window.innerHeight));
      }, 0);
    const keepFocusedTextVisible = () => {
      if (!compactLayout?.matches || studio.dataset.editorActive !== 'true' || !forward) return;
      const focused = document.activeElement;
      const inText = textPanel && !textPanel.hidden && textControls && !textControls.hidden && textControls.contains?.(focused);
      const inLayout = prototypeE && imagePanel && !imagePanel.hidden && imagePanel.contains?.(focused);
      if (!focused || !(inText || inLayout) || !window.scrollBy) return;
      const rect = focused.getBoundingClientRect?.();
      const forwardRect = forward.getBoundingClientRect?.();
      if (!rect || !forwardRect) return;
      const viewport = window.visualViewport;
      const compactBottom = prototypeE && editor.dataset.eEditorCompact === 'true'
        && editor.dataset.editorShortViewport !== 'true' ? artwork?.getBoundingClientRect?.().bottom || 0 : 0;
      const top = Math.max(viewport?.offsetTop || 0, stickyHeaderBottom(), compactBottom) + 12;
      const bottom = Math.min((viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight), forwardRect.top) - 12;
      if (bottom <= top) return;
      // A focused control can be scrolled by the browser into the fixed bar.
      // Move the page only when it is actually obscured; the next viewport
      // update sees it inside these bounds and makes no further movement.
      const delta = rect.height > bottom - top
        ? rect.top < top || rect.top > bottom ? rect.top - top : 0
        : rect.bottom > bottom ? rect.bottom - bottom : rect.top < top ? rect.top - top : 0;
      if (Math.abs(delta) > 1) window.scrollBy({ top: Math.ceil(delta), behavior: 'instant' });
    };
    const updateStickyArtwork = () => {
      stickyFrame = 0;
      const adjustFocus = focusVisibilityPending;
      focusVisibilityPending = false;
      // These values affect the visible controls only; the editor still owns
      // source pixels, transforms and the existing export/approval locks.
      if (forward && compactLayout?.matches && studio.dataset.editorActive === 'true') {
        const reserved = Math.ceil(forward.getBoundingClientRect?.().height || 96) + 16;
        editor.style.setProperty('--dim-editor-forward-space', `${reserved}px`);
        const viewport = window.visualViewport;
        const keyboardOffset = viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0;
        editor.style.setProperty('--dim-editor-keyboard-offset', `${Math.round(keyboardOffset)}px`);
        editor.dataset.editorShortViewport = String((viewport?.height || window.innerHeight) < 420);
        if (adjustFocus) keepFocusedTextVisible();
      } else {
        editor.style.removeProperty('--dim-editor-forward-space');
        editor.style.removeProperty('--dim-editor-keyboard-offset');
        delete editor.dataset.editorShortViewport;
      }
      if (!artwork) return;
      if (prototypeE) {
        const headerBottom = Math.ceil(stickyHeaderBottom());
        artwork.style.setProperty('--dim-e-sticky-top', `${headerBottom + 4}px`);
        const stickyHeight = editor.dataset.eEditorCompact === 'true' && editor.dataset.editorShortViewport !== 'true'
          ? artwork.getBoundingClientRect().height : 0;
        editor.style.setProperty('--dim-e-task-scroll-top', `${headerBottom + stickyHeight + 16}px`);
      }
      artwork.dataset.editorSticky = 'false';
      artwork.style.removeProperty('--dim-editor-sticky-top');
      if (compactLayout?.matches || studio.dataset.editorActive !== 'true') return;
      const top = Math.max(16, Math.ceil(stickyHeaderBottom()) + 12);
      const usableHeight = window.innerHeight - top - 16;
      if (usableHeight <= 0 || artwork.getBoundingClientRect().height > usableHeight) return;
      artwork.style.setProperty('--dim-editor-sticky-top', `${top}px`);
      artwork.dataset.editorSticky = 'true';
    };
    const scheduleStickyArtwork = () => {
      if (!artwork || stickyFrame) return;
      stickyFrame = window.requestAnimationFrame?.(updateStickyArtwork) || window.setTimeout(updateStickyArtwork, 0);
    };
    const scheduleFocusedTextVisibility = () => {
      focusVisibilityPending = true;
      scheduleStickyArtwork();
    };
    editor.addEventListener('focusin', scheduleFocusedTextVisibility);
    const syncArtworkDisclosure = () => {
      if (!artworkToggle || !artworkPreview) return;
      if (prototypeE) {
        const available = Boolean(compactLayout?.matches && studio.dataset.editorActive === 'true'
          && ['words', 'layout'].includes(editingTask));
        const compact = available && !largerEditingView;
        artworkToggle.hidden = !available;
        artworkPreview.hidden = false;
        editor.dataset.eEditorCompact = String(compact);
        artworkToggle.setAttribute('aria-expanded', String(!compact));
        artworkToggle.textContent = compact ? 'Show larger view' : 'Use compact editing view';
        const taskHasDirectEditing = editingTask === 'words' || editingTask === 'layout' && studio.dataset.wordsOnly !== 'true';
        const interactive = canOpenTask() && taskHasDirectEditing && !compact;
        const surfaceChanged = editor.dataset.eEditorInteractive !== String(interactive);
        editor.dataset.eEditorInteractive = String(interactive);
        canvas.setAttribute('tabindex', interactive ? canvasTabIndex || '0' : '-1');
        if (surfaceChanged) editor.dispatchEvent(new CustomEvent('dreamit:editor-surface-change', {
          bubbles: true, detail: { interactive, task: editingTask }
        }));
        syncTaskGuidance();
        // Size the existing display surface only. No image, canvas or transform
        // is copied; the renderer continues to own the same scene and export.
        let ratio = Number(editor.dataset.imageOverlayFrameAspect);
        if (!(ratio > 0)) {
          const parts = (editor.style.getPropertyValue?.('--dream-editor-aspect') || '').split('/').map(Number);
          ratio = parts.length === 2 && parts[1] > 0 ? parts[0] / parts[1] : Number(canvas.width) / Number(canvas.height);
        }
        editor.style.setProperty('--dim-e-artwork-aspect', String(ratio > 0 ? ratio : 1));
        return;
      }
      const available = compactLayout?.matches && studio.dataset.editorActive === 'true'
        && textPanel && !textPanel.hidden && textControls && !textControls.hidden;
      if (!available) artworkCollapsed = false;
      artworkToggle.hidden = !available;
      artworkPreview.hidden = Boolean(available && artworkCollapsed);
      artworkToggle.setAttribute('aria-expanded', String(!artworkPreview.hidden));
      artworkToggle.textContent = artworkPreview.hidden ? artworkToggle.dataset.showCopy : artworkToggle.dataset.hideCopy;
    };
    const toggleArtwork = () => {
      if (artworkToggle?.hidden) return;
      if (prototypeE) {
        largerEditingView = !largerEditingView;
        syncArtworkDisclosure();
        scheduleFocusedTextVisibility();
        return;
      }
      artworkCollapsed = !artworkCollapsed;
      syncArtworkDisclosure();
      scheduleStickyArtwork();
    };
    artworkToggle?.addEventListener('click', toggleArtwork);
    const onCompactLayoutChange = () => { placeCentre(); syncArtworkDisclosure(); scheduleFocusedTextVisibility(); };
    placeCentre();
    compactLayout?.addEventListener?.('change', onCompactLayoutChange);
    let artworkResizeObserver = null;
    if (artwork) {
      imageTips?.addEventListener('toggle', scheduleStickyArtwork);
      artworkResizeObserver = globalThis.ResizeObserver ? new ResizeObserver(scheduleFocusedTextVisibility) : null;
      artworkResizeObserver?.observe(artwork);
      if (forward) artworkResizeObserver?.observe(forward);
      if (prototypeE && textPanel) artworkResizeObserver?.observe(textPanel);
      if (prototypeE && imagePanel) artworkResizeObserver?.observe(imagePanel);
      window.addEventListener('resize', scheduleFocusedTextVisibility, { passive: true });
      window.visualViewport?.addEventListener?.('resize', scheduleFocusedTextVisibility, { passive: true });
      window.visualViewport?.addEventListener?.('scroll', scheduleStickyArtwork, { passive: true });
    }
    const allowedKeys = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
    const canAdjust = () => studio.dataset.editorActive === 'true'
      && ['loaded', 'dirty', 'ready'].includes(editor.dataset.editorState)
      && editor.dataset.replacementPending !== 'true'
      && editor.dataset.forwardPending !== 'true'
      && exportButton && !exportButton.disabled && exportButton.getAttribute('aria-busy') !== 'true';
    const canOpenTask = () => studio.dataset.editorActive === 'true'
      && ['loaded', 'dirty', 'ready'].includes(editor.dataset.editorState)
      && editor.dataset.replacementPending !== 'true' && editor.dataset.forwardPending !== 'true'
      && exportButton?.getAttribute('aria-busy') !== 'true';
    const setHidden = (element, hidden) => { if (element && element.hidden !== hidden) element.hidden = hidden; };
    const setCopy = (element, active, source, copy = element) => {
      const value = active ? copy?.dataset.adjustCopy : source ? copy?.dataset.sourceCopy : copy?.dataset.addCopy;
      if (element && value && element.textContent !== value) element.textContent = value;
    };

    const syncTaskSource = () => {
      if (!prototypeE) return;
      const wordsOnly = studio.dataset.wordsOnly === 'true';
      editor.dataset.eEditorWordsOnly = String(wordsOnly);
      const layoutButton = taskButtons.find(button => button.dataset.eEditorTask === 'layout');
      const panelId = (wordsOnly ? textPanel : imagePanel)?.id;
      if (panelId) layoutButton?.setAttribute('aria-controls', panelId);
    };
    const showTask = (task, userActivation = false, preserveFocus = false) => {
      if (!prototypeE || !['overview', 'words', 'layout'].includes(task)) return;
      if (task !== 'overview' && !canOpenTask()) return;
      const taskChanged = editingTask !== task;
      editingTask = task;
      syncTaskSource();
      const textTask = task === 'words' || task === 'layout' && studio.dataset.wordsOnly === 'true';
      // Selection and text editing remain owned by DIMTextControls/the editor.
      // Arrival and returning to the composed view do not change selection.
      if (task !== 'overview') editor.querySelector(`[data-dim-text-tab="${textTask ? 'text' : 'image'}"]`)?.click();
      if (textTask) {
        const title = taskButtons.find(button => button.dataset.eEditorTask === task)?.textContent;
        const textHeading = textPanel?.querySelector('.dim-text-controls__heading h2');
        if (textHeading && title) textHeading.textContent = title;
        if (task === 'layout') {
          const disclosure = textControls?.querySelector('[data-dim-text-disclosure="position"]');
          if (disclosure) disclosure.open = true;
        }
      }
      editor.dataset.eEditorTask = task;
      taskNavigation?.setAttribute('role', task === 'overview' ? 'group' : 'tablist');
      taskButtons.forEach(button => {
        const selected = button.dataset.eEditorTask === task;
        if (task === 'overview') {
          button.removeAttribute('role');
          button.removeAttribute('aria-selected');
          button.removeAttribute('tabindex');
        } else {
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-selected', String(selected));
          button.setAttribute('tabindex', selected ? '0' : '-1');
        }
        button.setAttribute('aria-expanded', String(selected));
      });
      const activeButton = taskButtons.find(button => button.dataset.eEditorTask === task);
      const activePanel = textTask ? textPanel : imagePanel;
      if (task !== 'overview' && activePanel) {
        activePanel.setAttribute('role', 'tabpanel');
        const label = activeButton?.getAttribute('id');
        if (label) activePanel.setAttribute('aria-labelledby', label);
      }
      syncArtworkDisclosure();
      if (task !== 'overview' && !preserveFocus) {
        // Focus the revealed non-editable heading so keyboard navigation stays in this task and
        // opening Words does not automatically raise a software keyboard.
        const taskHeading = textTask ? textPanel?.querySelector('[data-dim-text-empty]:not([hidden]) h2') || textPanel?.querySelector('.dim-text-controls__heading h2')
          : editor.querySelector('[data-dim-image-panel] legend');
        taskHeading?.setAttribute('tabindex', '-1');
        taskHeading?.focus?.({ preventScroll: true });
        taskHeading?.scrollIntoView?.({ block: 'center', behavior: 'instant' });
      }
      if (userActivation || taskChanged) editor.dispatchEvent(new CustomEvent('dreamit:editor-task-change', { bubbles: true, detail: { task } }));
      scheduleFocusedTextVisibility();
    };
    taskButtons.forEach(button => button.addEventListener('click', () => showTask(button.dataset.eEditorTask, true)));
    taskButtons.forEach((button, index) => button.addEventListener('keydown', event => {
      if (editingTask === 'overview' || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? taskButtons.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + taskButtons.length) % taskButtons.length;
      const target = taskButtons[next];
      if (target.disabled) return;
      showTask(target.dataset.eEditorTask, true);
      target.focus({ preventScroll: true });
    }));
    const syncDesignContext = () => {
      if (!prototypeE) return;
      const identity = [selectedName?.textContent, selectedVariant?.textContent].filter(value => value?.trim()).join(' — ');
      if (designSummary) designSummary.textContent = identity ? `Artwork area for ${identity}. Check placement in Preview before approving.` : 'Artwork area. Check placement in Preview before approving.';
      const photo = selectedImage?.getAttribute?.('src');
      const separatePhoto = Boolean(photo && !selectedImage.hidden && !activeOverlay);
      setHidden(catalogue, !separatePhoto);
      if (catalogueImage && separatePhoto) {
        if (catalogueImage.getAttribute('src') !== photo) catalogueImage.setAttribute('src', photo);
        catalogueImage.alt = identity ? `${identity} catalogue photograph` : 'Catalogue photograph';
      } else catalogueImage?.removeAttribute('src');
    };
    if (prototypeE) showTask('overview');

    const findOverlay = (context) => {
      let identity;
      try { identity = JSON.parse(context); } catch (_) { return null; }
      if (!Array.isArray(identity) || identity.length !== 6 || !Array.isArray(overlayMap)
        || !identity.slice(3).every((value) => Number.isFinite(value) && value > 0)) return null;
      const matches = overlayMap.filter((item) => item.contexts?.some((candidate) =>
        candidate.length === 6 && candidate.every((value, index) => value === identity[index])));
      if (matches.length !== 1) return null;
      const item = matches[0], view = item.viewBox, rect = item.printRect;
      if (!Array.isArray(view) || view.length !== 4 || !view.every(Number.isFinite)
        || !rect || !['x', 'y', 'width', 'height'].every((key) => Number.isFinite(rect[key]))
        || view[2] <= 0 || view[3] <= 0 || rect.width <= 0 || rect.height <= 0
        || rect.x < view[0] || rect.y < view[1] || rect.x + rect.width > view[0] + view[2]
        || rect.y + rect.height > view[1] + view[3] || !item.src
        || Math.abs((rect.width / rect.height) / (identity[4] / identity[5]) - 1) > 0.001) return null;
      return item;
    };
    const syncOverlay = () => {
      if (!overlay || !overlayToggle || !overlayControls || !artboard) return;
      // The resolved identity owns any shown coordinate frame. Temporary editor
      // locks must not replace that frame or request the same guide again.
      const context = studio.dataset.editorActive === 'true' ? editor.dataset.imageOverlayContext || '' : '';
      if (context !== overlayContext) {
        overlayContext = context;
        overlayShown = false;
        loadedOverlay = null;
        activeOverlay = findOverlay(context);
        const request = ++overlayRequest;
        if (activeOverlay) {
          const requested = activeOverlay, image = new Image();
          image.onload = () => {
            if (request !== overlayRequest || context !== editor.dataset.imageOverlayContext || !image.naturalWidth) return;
            loadedOverlay = requested;
            overlay.src = image.src;
            syncOverlay();
          };
          image.onerror = () => {
            if (request !== overlayRequest || context !== editor.dataset.imageOverlayContext) return;
            loadedOverlay = null;
            // A guide that cannot decode is not a usable schematic viewport.
            // Keep the normal canvas available until a changed identity resolves.
            activeOverlay = null;
            syncOverlay();
          };
          image.src = requested.src;
        }
      }
      const resolved = Boolean(context && activeOverlay && loadedOverlay === activeOverlay);
      const eligible = Boolean(canAdjust() && resolved);
      // Temporary locks disable the toggle but retain the customer's explicit
      // guide choice. Hidden guides leave the normal output-aspect canvas full-size.
      const shown = Boolean(resolved && overlayShown);
      const frame = shown ? { overlay: activeOverlay, viewport: {
        x: activeOverlay.viewBox[0], y: activeOverlay.viewBox[1],
        width: activeOverlay.viewBox[2], height: activeOverlay.viewBox[3]
      } } : null;
      const frameAspect = frame ? String(frame.viewport.width / frame.viewport.height) : '';
      const frameChanged = (editor.dataset.imageOverlayFrameAspect || '') !== frameAspect;
      if (frame) {
        const view = frame.overlay.viewBox, rect = frame.overlay.printRect, viewport = frame.viewport;
        const values = { aspect: `${viewport.width} / ${viewport.height}`,
          left: `${100 * (rect.x - viewport.x) / viewport.width}%`, top: `${100 * (rect.y - viewport.y) / viewport.height}%`,
          width: `${100 * rect.width / viewport.width}%`, height: `${100 * rect.height / viewport.height}%`,
          guideLeft: `${100 * (view[0] - viewport.x) / viewport.width}%`, guideTop: `${100 * (view[1] - viewport.y) / viewport.height}%`,
          guideWidth: `${100 * view[2] / viewport.width}%`, guideHeight: `${100 * view[3] / viewport.height}%` };
        artboard.dataset.imageOverlayFrame = 'true';
        artboard.dataset.imageOverlayCamera = String(viewport.width !== view[2] || viewport.height !== view[3]);
        editor.dataset.imageOverlayFrameAspect = frameAspect;
        Object.entries(values).forEach(([key, value]) => artboard.style.setProperty(`--image-overlay-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`, value));
      } else {
        artboard.dataset.imageOverlayFrame = 'false';
        artboard.dataset.imageOverlayCamera = 'false';
        delete editor.dataset.imageOverlayFrameAspect;
        ['aspect', 'left', 'top', 'width', 'height', 'guide-left', 'guide-top', 'guide-width', 'guide-height']
          .forEach((key) => artboard.style.removeProperty?.(`--image-overlay-${key}`));
      }
      if (frameChanged && typeof CustomEvent === 'function') {
        editor.dispatchEvent?.(new CustomEvent('dreamit:editor-presentation-frame-change', { bubbles: false }));
      }
      overlayControls.hidden = !eligible;
      overlayToggle.disabled = !eligible;
      overlayToggle.setAttribute('aria-pressed', String(shown));
      overlay.hidden = !shown;
      // The shown guide establishes only the displayed coordinate frame. The
      // editor continues to own artwork transforms and export dimensions.
      artboard.dataset.imageOverlayVisible = String(shown);
      if (overlayText) {
        overlayText.textContent = editor.dataset.imageOverlayText || '';
        overlayText.hidden = !shown || !overlayText.textContent.trim();
        if (!overlayText.hidden) overlayToggle.setAttribute('aria-describedby', overlayText.id);
        else overlayToggle.removeAttribute('aria-describedby');
      }
      syncDesignContext();
    };
    overlayToggle?.addEventListener('click', () => {
      if (!canAdjust() || !activeOverlay || loadedOverlay !== activeOverlay
        || editor.dataset.imageOverlayContext !== overlayContext) return;
      overlayShown = !overlayShown;
      syncOverlay();
    });

    const synchronise = () => {
      const active = studio.dataset.editorActive === 'true';
      const source = !active && studio.dataset.sourceMode === 'true';
      setCopy(headingText, active, source, heading);
      if (sourceHeading) {
        setHidden(headingText, source);
        setHidden(sourceHeading, !source);
        heading.setAttribute('aria-label', headingText.textContent);
      }
      setHidden(navigation, false);
      setHidden(sourceIntro, !source);
      const routes = globalThis.DreamItJourneyRoutes;
      const family = studio.dataset.journeyFamily || routes?.routeFamily({ search: globalThis.location?.search || '', sourceMode: source });
      const productSelected = selectedSummary && !selectedSummary.hidden;
      const currentStep = active || family === 'source' && productSelected ? 4 : 3;
      const completed = new Set([
        ...((guidedIllustration && !guidedIllustration.hidden) || ideaInput?.value?.trim() ? [1] : []),
        ...(productSelected ? [2] : []),
        ...(active || family === 'source' && productSelected && ideaInput?.value?.trim() ? [3] : [])
      ]);
      if (routes?.projectProgress) routes.projectProgress(navigation, { family, currentStep, completed: [...completed] });
      else if (navigation) navigation.dataset.journeyStep = String(currentStep);
      try { syncOverlay(); } catch (_) { /* Optional overlay presentation must not block the journey stage. */ }
      syncDesignContext();
      syncTaskSource();
      taskButtons.forEach(button => { button.disabled = !canOpenTask()
        || button.dataset.eEditorTask === 'words' && (!textControls || textControls.hidden); });
      nudges.forEach((button) => { button.disabled = !canAdjust(); });
      setHidden(retry, !(active && uploadStatus && !uploadStatus.hidden && uploadStatus.dataset.state === 'error' && canAdjust()));
      if (!active) setHidden(restored, true);
      syncArtworkDisclosure();
      scheduleStickyArtwork();
    };

    nudges.forEach((button) => button.addEventListener('click', () => {
      const key = button.dataset.uxEditorNudge;
      if (button.disabled || !canAdjust() || !allowedKeys.has(key)) return;
      editor.dispatchEvent(new CustomEvent('dreamit:editor-nudge', { bubbles: true, detail: { key } }));
    }));
    retry?.addEventListener('click', () => {
      if (!retry.hidden && canAdjust()) exportButton.click();
    });
    const ready = (event) => {
      if (event.target !== editor) return;
      artworkCollapsed = false;
      if (prototypeE) {
        if (event.detail?.restored !== true) largerEditingView = false;
        const restoredTask = editor.dataset.eEditorRestoreTask;
        delete editor.dataset.eEditorRestoreTask;
        const task = event.detail?.restored === true
          ? ['words', 'layout'].includes(restoredTask) ? restoredTask : editingTask : 'overview';
        const keepFocus = event.detail?.restored === true && task === editingTask && editor.contains?.(document.activeElement);
        showTask(task, false, keepFocus);
      }
      setHidden(restored, event.detail?.restored !== true);
      synchronise();
    };
    editor.addEventListener('dreamit:editor-ready', ready);
    const observer = new MutationObserver(synchronise);
    observer.observe(studio, { attributes: true, attributeFilter: ['data-editor-active', 'data-source-mode', 'data-journey-family', 'data-words-only'] });
    if (selectedSummary) observer.observe(selectedSummary, { attributes: true, attributeFilter: ['hidden'] });
    if (prototypeE && selectedSummary) observer.observe(selectedSummary, { childList: true, characterData: true, subtree: true,
      attributes: true, attributeFilter: ['src', 'hidden'] });
    if (guidedIllustration) observer.observe(guidedIllustration, { attributes: true, attributeFilter: ['hidden'] });
    ideaInput?.addEventListener('input', synchronise);
    observer.observe(editor, { attributes: true, attributeFilter: ['data-editor-state', 'data-forward-pending', 'data-replacement-pending', 'data-image-overlay-context', 'data-image-overlay-text'] });
    if (exportButton) observer.observe(exportButton, { attributes: true, attributeFilter: ['disabled', 'aria-busy'] });
    if (uploadStatus) observer.observe(uploadStatus, { attributes: true, attributeFilter: ['hidden', 'data-state'] });
    if (textPanel) observer.observe(textPanel, { attributes: true, attributeFilter: ['hidden'] });
    if (textControls) observer.observe(textControls, { attributes: true, attributeFilter: ['hidden'] });
    if (prototypeE && textCount) observer.observe(textCount, { childList: true, characterData: true, subtree: true });
    const pageshow = () => synchronise();
    globalThis.addEventListener?.('pageshow', pageshow);
    const unload = (event) => {
      if (!event.target?.contains(studio)) return;
      observer.disconnect();
      overlayRequest += 1;
      if (stickyFrame) {
        window.cancelAnimationFrame?.(stickyFrame);
        window.clearTimeout?.(stickyFrame);
      }
      artworkResizeObserver?.disconnect();
      compactLayout?.removeEventListener?.('change', onCompactLayoutChange);
      imageTips?.removeEventListener('toggle', scheduleStickyArtwork);
      artworkToggle?.removeEventListener('click', toggleArtwork);
      if (artwork) {
        window.removeEventListener('resize', scheduleFocusedTextVisibility);
        window.visualViewport?.removeEventListener?.('resize', scheduleFocusedTextVisibility);
        window.visualViewport?.removeEventListener?.('scroll', scheduleStickyArtwork);
      }
      globalThis.removeEventListener?.('pageshow', pageshow);
      editor.removeEventListener('dreamit:editor-ready', ready);
      editor.removeEventListener('focusin', scheduleFocusedTextVisibility);
      ideaInput?.removeEventListener('input', synchronise);
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
    synchronise();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
  document.addEventListener('shopify:section:load', initialise);
})();
